import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, getIn, useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { EMPTY_STRING } from '@e-socna/empty';
import { buildMissionDocumentTitle } from '@e-socna/folder-documents';
import type { Id, PrintStyleId, ReportWorkflowId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { filterWorkflowsOfMissionWorkflow } from '../../utils/workflows-of-mission-workflow.util';
import { selectableWorkflowsOf } from '../../utils/selectable-workflows.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { CodeSuggest } from '../../components/CodeSuggest';
import { IdSelect } from '../../components/IdSelect';
import { IdAutocomplete } from '../../components/IdAutocomplete';
import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';
import type { MissionReportWithItsInfo } from '../../pages/MissionReport/types/mission-report-with-its-info.type';
import type { IdOption } from '../../components/IdSelect';

import { buildDocumentTemplateOptions } from '../DocumentTemplates/utils/build-document-template-options.util';
import { FormVariant } from '../enums/variant.enum';
import type { MissionDocumentTemplate } from '../DocumentTemplates/types/mission-document-template.type';

import { useCodeSuggestion } from './hooks/useCodeSuggestion.hook';

interface Props {
  readonly formVariant: FormVariant;
  readonly onCancel?: () => void;
  readonly reportTemplates: readonly MissionDocumentTemplate[];
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel: string;
  readonly suggestedTemplateIds: ReadonlySet<Id>;
}

export const MissionReportInfoFormFields = ({
  formVariant,
  onCancel: handleCancel,
  reportTemplates,
  setMustBlockAction,
  showCancelButton,
  submitLabel,
  suggestedTemplateIds,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { mission } = useMissionReadyContext();
  const { printStyles, reportWorkflows } = useReferencesContext();

  const [isAlertOpen, setIsAlertOpen] = useState(true);
  const closeAlert = useCallback(() => {
    setIsAlertOpen(false);
  }, []);

  const [isTitleTouched, setIsTitleTouched] = useState(false);

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<MissionReportWithItsInfo>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  const handleWorkflowIdChange = useCallback(
    (id: ReportWorkflowId | typeof EMPTY_STRING) => {
      void setFieldValue('info.workflowId', id);
    },
    [setFieldValue],
  );

  const handleTemplateIdChange = useCallback(
    (id: Id | typeof EMPTY_STRING) => {
      void setFieldValue('templateId', id || EMPTY_STRING);
    },
    [setFieldValue],
  );

  const handleTitleChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setIsTitleTouched(true);
      handleChange(event);
    },
    [handleChange],
  );

  const handlePrintStyleIdChange = useCallback(
    (id: PrintStyleId | typeof EMPTY_STRING) => {
      void setFieldValue('printing.printSettings.printStyleId', id);
    },
    [setFieldValue],
  );

  const willCreateDocumentImmediately =
    formVariant === FormVariant.Creation &&
    !!reportWorkflows.find(
      (reportWorkflow) => reportWorkflow._id === values.info.workflowId,
    )?.initAction.settings.autoCreateDocumentIfNotExists;

  const templateOptions = useMemo<readonly IdOption<Id>[]>(
    () => buildDocumentTemplateOptions(reportTemplates, suggestedTemplateIds),
    [reportTemplates, suggestedTemplateIds],
  );

  useEffect(() => {
    if (!willCreateDocumentImmediately || isTitleTouched) {
      return;
    }

    const template = reportTemplates.find(
      (candidate) => candidate._id === values.templateId,
    );

    setFieldValueOnNextTick(
      'title',
      buildMissionDocumentTitle({
        missionTitle: mission.consolidation.title,
        templateTitle: template?.document.title,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    willCreateDocumentImmediately,
    values.templateId,
    reportTemplates,
    setFieldValueOnNextTick,
  ]);

  return (
    <Form
      onSubmit={formikSubmit}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            {!!showCancelButton && (
              <Button onClick={handleCancel} size="large" variant="outlined">
                Annuler
              </Button>
            )}
          </Stack>
        }
        title={
          formVariant === FormVariant.Modification
            ? 'Informations liées au Rapport'
            : null
        }
      >
        <Grid container spacing={2}>
          <Grid size={12} sx={{ pt: 1, pb: 1 }}>
            <IdSelect<ReportWorkflowId, IdOption<ReportWorkflowId>>
              error={!!(errors.info?.workflowId && touched.info?.workflowId)}
              helperText={
                !!(errors.info?.workflowId && touched.info?.workflowId) &&
                (getIn(errors, 'info.workflowId') as string)
              }
              id="mission-report-info-workflow-id"
              label="Workflow"
              name="info.workflowId"
              onBlur={handleBlur}
              onChange={handleWorkflowIdChange}
              options={filterWorkflowsOfMissionWorkflow(
                selectableWorkflowsOf(reportWorkflows),
                mission.workflowId,
              ).map((reportWorkflow) => ({
                id: reportWorkflow._id,
                label: reportWorkflow.label,
              }))}
              required
              value={values.info.workflowId as ReportWorkflowId}
            />
          </Grid>
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
          {!!willCreateDocumentImmediately && (
            <>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <IdAutocomplete<Id, IdOption<Id>>
                  label="Modèle"
                  name="templateId"
                  noOptionsText="Aucun modèle disponible"
                  onChange={handleTemplateIdChange}
                  options={templateOptions}
                  value={values.templateId ?? EMPTY_STRING}
                />
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <TextField
                  error={!!(errors.title && touched.title)}
                  fullWidth
                  helperText={getTouchedError(errors, touched, 'title')}
                  id="document-title"
                  label="Titre du document"
                  name="title"
                  onBlur={handleBlur}
                  onChange={handleTitleChange}
                  required
                  type="text"
                  value={values.title}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <CodeSuggest useCodeSuggestion={useCodeSuggestion} />
              </Grid>
            </>
          )}
          {formVariant === FormVariant.Modification && (
            <>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <Typography sx={{ mt: 2, mb: 2 }} variant="h5">
                  Document
                </Typography>
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
                {!!isAlertOpen && (
                  <Alert
                    action={
                      <IconButton
                        aria-label="Fermer"
                        color="warning"
                        onClick={closeAlert}
                        size="small"
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    severity="warning"
                    sx={{ mb: 2 }}
                  >
                    La modification du modèle entraînera la réinitialisation du
                    contenu du document.
                  </Alert>
                )}
                <IdSelect<Id, IdOption<Id>>
                  error={!!(errors.templateId && touched.templateId)}
                  helperText={
                    !!(errors.templateId && touched.templateId) &&
                    errors.templateId
                  }
                  id="document-template-id"
                  label="Modèle"
                  name="templateId"
                  nullable
                  onBlur={handleBlur}
                  onChange={handleTemplateIdChange}
                  options={reportTemplates.map((template) => ({
                    id: template._id,
                    label: template.label,
                  }))}
                  value={values.templateId ?? EMPTY_STRING}
                />
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <TextField
                  error={!!(errors.title && touched.title)}
                  fullWidth
                  helperText={getTouchedError(errors, touched, 'title')}
                  id="document-title"
                  label="Titre du document"
                  name="title"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  required
                  type="text"
                  value={values.title}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <Typography sx={{ mt: 2, mb: 2 }} variant="h5">
                  Options d&apos;Impression
                </Typography>
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <FormControl
                  error={
                    !!(
                      errors.printing?.printSettings?.addHeaders &&
                      touched.printing?.printSettings?.addHeaders
                    )
                  }
                  fullWidth
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.printing.printSettings.addHeaders}
                        id="printing-print-settings-add-headers"
                        name="printing.printSettings.addHeaders"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    }
                    label="Inclure entêtes de pages"
                  />
                  {!!(
                    errors.printing?.printSettings?.addHeaders &&
                    touched.printing?.printSettings?.addHeaders
                  ) && (
                    <FormHelperText>
                      {errors.printing.printSettings.addHeaders}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <FormControl
                  error={
                    !!(
                      errors.printing?.printSettings?.addFooters &&
                      touched.printing?.printSettings?.addFooters
                    )
                  }
                  fullWidth
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.printing.printSettings.addFooters}
                        id="printing-print-settings-add-footers"
                        name="printing.printSettings.addFooters"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    }
                    label="Inclure pieds de pages"
                  />
                  {!!(
                    errors.printing?.printSettings?.addFooters &&
                    touched.printing?.printSettings?.addFooters
                  ) && (
                    <FormHelperText>
                      {errors.printing.printSettings.addFooters}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                <IdSelect<PrintStyleId, IdOption<PrintStyleId>>
                  error={
                    !!(
                      errors.printing?.printSettings?.printStyleId &&
                      touched.printing?.printSettings?.printStyleId
                    )
                  }
                  helperText={
                    !!(
                      errors.printing?.printSettings?.printStyleId &&
                      touched.printing?.printSettings?.printStyleId
                    ) &&
                    (getIn(
                      errors,
                      'printing.printSettings.printStyleId',
                    ) as string)
                  }
                  id="printing-print-style-id"
                  label="Style d'impression"
                  name="printing.printSettings.printStyleId"
                  onBlur={handleBlur}
                  onChange={handlePrintStyleIdChange}
                  options={printStyles.map((printStyle) => ({
                    id: printStyle._id,
                    label: printStyle.label,
                  }))}
                  required
                  value={
                    values.printing.printSettings.printStyleId as PrintStyleId
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </FormLayout>
    </Form>
  );
};
